import type { BilingualFieldType, ContentLang } from '@yoyoclub/shared';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

/** Thrown for any non-success response; the forms treat every failure the same way. */
export class TranslationUnavailableError extends Error {
  constructor(public readonly reason: string) {
    super('Translation unavailable');
  }
}

let statusPromise: Promise<boolean> | null = null;

/** Whether the translation switch is on. Cached per page load; failures count as off. */
export function isTranslationEnabled(): Promise<boolean> {
  if (!statusPromise) {
    statusPromise = fetch(`${API_URL}/translate/status`)
      .then(async (res) => (res.ok ? Boolean((await res.json()).data?.enabled) : false))
      .catch(() => false);
  }
  return statusPromise;
}

// Identical source text translates once per page (repeated sessions share a
// description), and concurrent callers share the in-flight request.
const cache = new Map<string, Promise<string>>();

export type ZhScript = 'simplified' | 'traditional';

/**
 * Translate one field. `zhScript` sets the script of Chinese output from the
 * caller's current site language (PRD §6.2); it has no effect into English.
 */
export function translateText(
  fieldType: BilingualFieldType,
  fromLang: ContentLang,
  text: string,
  token: string,
  zhScript: ZhScript = 'simplified'
): Promise<string> {
  const key = `${fieldType}|${fromLang}|${zhScript}|${text}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const p = (async () => {
    const res = await fetch(`${API_URL}/translate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(
        fromLang === 'EN' ? { fieldType, fromLang, text, zhScript } : { fieldType, fromLang, text }
      ),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body?.success || typeof body.data?.text !== 'string') {
      throw new TranslationUnavailableError(body?.reason || body?.error || `HTTP ${res.status}`);
    }
    return body.data.text as string;
  })();
  cache.set(key, p);
  p.catch(() => cache.delete(key));
  return p;
}

/** Test helper. */
export function __resetTranslationApiForTests() {
  statusPromise = null;
  cache.clear();
}
